import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ChevronRight, Crown, Sparkles } from "lucide-react";
import { VerdictButton } from "../../pages/Dossier";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });

// Hub card listing Coach-released Elite Scouting Dossiers.
export default function EliteDossiersCard({ token, isElite, onLocked }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/dossiers`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data: d }) => setData(d)).catch(() => {});
  }, [token]);
  if (!data) return null;
  const items = data.items || [];
  if (!isElite && items.length === 0) {
    return (
      <button data-testid="elite-dossiers-locked" onClick={onLocked} className="w-full text-left mb-8 rounded-xl border border-white/10 bg-[#141414] p-5 hover:border-[#D4AF37]/50 transition-colors">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/45"><Crown size={11} style={{ color: GOLD }} /> Elite Feature</div>
        <div className="font-headings text-xl uppercase tracking-wide mt-1">Monthly Scouting Dossier</div>
        <p className="text-sm text-white/55 mt-1">Elite members receive a Coach-released monthly performance report — animated card, activity heatmap, training focus and start-vs-end radar.</p>
      </button>
    );
  }
  return (
    <motion.section data-testid="elite-dossiers-card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8 rounded-xl border overflow-hidden elite-carbon-bg grain relative" style={{ borderColor: `${GOLD}44`, boxShadow: `0 20px 60px rgba(0,0,0,.5), inset 0 1px 0 ${GOLD}55` }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(500px 200px at 0% 0%, ${GOLD}1f, transparent 70%)` }} />
      <div className="relative p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: GOLD }}><FileText size={12} /> Elite Scouting Dossiers</div>
            <h2 className="font-headings text-2xl uppercase tracking-wide mt-1">Monthly Performance Reports</h2>
          </div>
          <Crown size={22} style={{ color: GOLD, filter: `drop-shadow(0 0 10px ${GOLD})` }} />
        </div>
        {items.length === 0 ? (
          <p data-testid="elite-dossiers-empty" className="text-sm text-white/55">Your first dossier lands here when Coach releases it at month's end — keep logging reps so the heatmap glows.</p>
        ) : (
          <div className="space-y-2.5">
            {items.map((d) => (
              <div key={d.id} data-testid={`dossier-row-${d.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border p-3.5" style={{ borderColor: `${GOLD}2a`, background: "rgba(0,0,0,0.4)" }}>
                <button data-testid={`dossier-open-${d.id}`} onClick={() => navigate(`/dossier/${d.id}`)} className="flex items-center gap-3 flex-1 text-left min-w-0">
                  <span className="w-11 h-11 rounded-md flex items-center justify-center font-cover text-lg text-black shrink-0" style={{ background: `linear-gradient(135deg, #F0D77A, ${GOLD} 60%, #B8912E)` }}>{d.dossier_no}</span>
                  <span className="min-w-0">
                    <span className="font-headings text-lg uppercase tracking-wide leading-none block truncate">{monthLabel(d.month)}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45 flex items-center gap-1.5 mt-1">Released {new Date(d.released_at).toLocaleDateString()} {!d.seen && <span className="inline-flex items-center gap-1" style={{ color: GOLD }}><Sparkles size={9} /> New</span>}</span>
                  </span>
                  <ChevronRight size={16} className="text-white/30 ml-auto shrink-0 hidden sm:block" />
                </button>
                {d.has_voice && <VerdictButton dossierId={d.id} token={token} className="w-full sm:w-auto justify-center" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.section>
  );
}
